import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Box,
  Grid,
  Typography,
  Popover,
  Button,
  FormControlLabel,
  TextField,
  Checkbox
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteField from '../../otherComponent/DeleteStep';

interface FileComponentProps {
  values: any;
  index: number;
  setFieldValue: (key: string, value: any) => void;
  onSubmit: (data: any) => void;
  handleChange: (e: any) => void;
  handleBlur: (e: any) => void;
  selectedNodeData?: any;
  onDeleteField?: (fieldIndex: number) => void;
  toSentenceCase: (str: string) => string;
  fieldTypeSelector?: ReactNode;
  isSettingsOpen?: boolean;
  onSettingsOpen?: (fieldIndex: number) => void;
  onSettingsClose?: () => void;
}

export default function FileComponent({
  values,
  index,
  setFieldValue,
  onSubmit,
  handleChange,
  handleBlur,
  selectedNodeData,
  onDeleteField,
  toSentenceCase,
  fieldTypeSelector,
  isSettingsOpen,
  onSettingsOpen,
  onSettingsClose
}: FileComponentProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const settingsButtonRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (typeof isSettingsOpen !== 'boolean') {
      return;
    }

    if (!isSettingsOpen) {
      setAnchorEl(null);
      return;
    }

    if (settingsButtonRef.current) {
      setAnchorEl(settingsButtonRef.current);
    }
  }, [isSettingsOpen]);

  const handleEditClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    onSettingsOpen?.(index);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
    onSettingsClose?.();
  };

  const open = Boolean(anchorEl);
  const id = open ? 'edit-file-popover' : undefined;

  const settings = values.fields[index]?.settings || {};
  const answer = values.fields[index]?.answer || [];

  const maxImages = parseInt(settings.maxImages || 1);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    const limitedFiles = files.slice(0, maxImages);
    setFieldValue(`fields.${index}.answer`, limitedFiles);
  };

  
  return (
    <Box sx={{ width: '100%' }}>
      <Grid container alignItems="center" spacing={1}>
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                flexGrow: 1,
                maxWidth: 'calc(100% - 40px)',
                py: 1,
                px: 1,
                border: '1px solid #ccc',
                borderRadius: 1,
                backgroundColor: '#fafafa',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                minWidth: 0
              }}
            >
              <Typography
                variant="body1"
                sx={{ flexGrow: 1, minWidth: 0, whiteSpace: 'normal', wordBreak: 'break-word' }}
              >
                {toSentenceCase(values.fields[index].question || 'Upload File(s)')}
              </Typography>
              <UploadFileIcon fontSize="small" color="action" />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box ref={settingsButtonRef} sx={{ cursor: 'pointer', color: 'primary.main' }} onClick={handleEditClick}>
                <SettingsIcon fontSize="medium" />
              </Box>
              <DeleteField 
                onDeleteField={() => onDeleteField?.(index)}
                selectedNodeData={selectedNodeData}
                fieldIndex={index}
                fieldData={values.fields[index]}
              />
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} hidden>
          <Button variant="outlined" component="label" fullWidth size="small">
            {answer.length > 0 ? `${answer.length} file(s) selected` : 'Upload File(s)'}
            <input
              type="file"
              hidden
              multiple={maxImages > 1}
              accept={settings.accept || '*'}
              onChange={handleFileChange}
            />
          </Button>
        </Grid>
      </Grid>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        PaperProps={{ sx: { width: 350, p: 2 } }}
      >
        <Box>
          <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2} gap={1}>
            <Typography variant="subtitle1">File Settings</Typography>
            {fieldTypeSelector ? (
              <Box sx={{ minWidth: 120 }}>
                {fieldTypeSelector}
              </Box>
            ) : null}
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Label"
                name={`fields.${index}.question`}
                value={values.fields[index].question || ''}
                onChange={handleChange}
                onBlur={handleBlur}
                size="small"
              />
            </Grid>

            {/* <Grid item xs={12} sx={{ mt: -1 }}  >
              <TextField
                fullWidth
                label="Description"
                name={`fields.${index}.description`}
                value={values.fields[index].description || ''}
                onChange={handleChange}
                onBlur={handleBlur}
                size="small"
              />
            </Grid> */}

            {/* <Grid item xs={12}>
              <TextField
                fullWidth
                label="Allowed Types"
                value={settings.accept || ''}
                onChange={(e) =>
                  setFieldValue(`fields.${index}.settings.accept`, e.target.value)
                }
                size="small"
                placeholder="e.g. image/* or .png,.jpg"
              />
            </Grid> */}

            {/* <Grid item xs={12}>
              <TextField
                fullWidth
                label="No. of Images"
                type="number"
                value={settings.maxImages || 1}
                onChange={(e) =>
                  setFieldValue(`fields.${index}.settings.maxImages`, e.target.value)
                }
                size="small"
              />
            </Grid> */}
 
            <Grid item xs={12} sx={{ mt: -1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={settings.required || false}
                    onChange={(e) =>
                      setFieldValue(`fields.${index}.settings.required`, e.target.checked)
                    }
                  />
                }
                label="Required"
              />
            </Grid>

            <Grid item xs={12}>
              <Box mt={2} display="flex" justifyContent="flex-end">
                <Button size="small" variant="contained" onClick={handleClosePopover}>
                  Save
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Popover>
    </Box>
  );
}

